import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import Card from '@dashboard/components/Card';
import Spinner from '@dashboard/components/Spinner';
import Button from '@dashboard/components/Button';
import { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import DialogOld from '@dashboard/components/DialogOld';
import Link from '@dashboard/components/Link';
import ConnectStripeDialog from '@dashboard/components/ConnectStripeDialog';
import Logo from '@dashboard/components/Logo';
import { fetcher } from '@dashboard/services/request';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import checkIsConnectedToStripe from '@dashboard/services/seller/checkIsConnectedToStripe';
import { pageKey, pagesKey } from '@dashboard/lib/queryKeys';
import StripeLogo from './stripe.png';
import getShouldSyncSubscription from './getShouldSyncSubscription';
import CheckoutShareDirect from './scenes/CheckoutShareDirect';
import CheckoutShareMethods from './scenes/CheckoutShareMethods';

const CheckoutShareDialog = ({ checkoutId, isOpen = false, onClose }) => {
  return (
    <DialogOld isOpen={isOpen} onClose={onClose} size="3xl">
      <CheckoutShareDialogContent checkoutId={checkoutId} onClose={onClose} />
    </DialogOld>
  );
};

function CheckoutShareDialogContent({ checkoutId, onClose }) {
  const { token, seller, sellerId } = useAuth();
  const [connectWithStripeDialogOpen, setConnectWithStripeDialogOpen] = useState(false);
  const [syncSubscriptionDialogOpen, setSyncSubscriptionDialogOpen] = useState(false);

  const {
    isLoading,
    isError,
    data: page,
  } = useQuery({
    queryKey: pageKey(checkoutId),
    queryFn: () => fetcher(`/sellers/${sellerId}/pages/${checkoutId}`, token),
    enabled: !!token,
  });

  useEffect(() => {
    if (isEmpty(page?.product)) return;
    if (isEmpty(seller)) return;
    if (!checkIsConnectedToStripe(seller)) return;

    if (page.product?.type === 'subscription' && !page.product?.planActive) {
      setSyncSubscriptionDialogOpen(true);
    }
  }, [page?.product?.type, page?.product?.planActive]);

  if (isLoading) {
    return (
      <Card>
        <Spinner />
      </Card>
    );
  }

  if (!isLoading && isError) {
    return <Card>Something went wrong</Card>;
  }

  const shouldSyncSubscription = page?.product && getShouldSyncSubscription(page, seller);

  return (
    <div className="text-left h-full flex-1 flex flex-col">
      {connectWithStripeDialogOpen && (
        <ConnectStripeDialog
          onClose={() => setConnectWithStripeDialogOpen(false)}
          defaultIsOpen={true}
        />
      )}

      {syncSubscriptionDialogOpen && (
        <SyncSubscriptionDialog
          onClose={() => setSyncSubscriptionDialogOpen(false)}
          product={page.product}
        />
      )}

      {!syncSubscriptionDialogOpen && (
        <div className="flex flex-col flex-1">
          <h2 className="mb-4 text-2xl">Share & embed</h2>

          {!checkIsConnectedToStripe(seller) && page.type === 'checkout' && (
            <ConnectWithStripeBanner onClick={() => setConnectWithStripeDialogOpen(true)} />
          )}

          {shouldSyncSubscription && (
            <SyncSubscriptionBanner onClick={() => setSyncSubscriptionDialogOpen(true)} />
          )}

          <CheckoutShareDirect page={page} seller={seller} closeDialog={onClose} />

          <CheckoutShareMethods page={page} seller={seller} />
        </div>
      )}
    </div>
  );
}

function ConnectWithStripeBanner({ onClick }) {
  return (
    <div
      className="p-2 rounded border border-chocolate-600 bg-chocolate-50 flex items-center gap-x-2 mt-4 mb-4 hover:bg-chocolate-100 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <ExclamationCircleIcon className="h-6 w-6 text-chocolate-600" />
      <span>Connect with Stripe to process payments</span>
      <div className="font-medium text-chocolate-600 ml-auto px-2 flex-1 whitespace-nowrap text-right">
        Connect
      </div>
    </div>
  );
}

function SyncSubscriptionBanner({ onClick }) {
  return (
    <div
      className="p-2 rounded border border-chocolate-600 bg-chocolate-50 flex items-center gap-x-2 mt-4 mb-8 hover:bg-chocolate-100 cursor-pointer transition-colors"
      onClick={onClick}
    >
      <ExclamationCircleIcon className="h-6 w-6 text-chocolate-600" />
      <span>Sync your subscription prices with Stripe to process payments</span>
      <div className="font-medium text-chocolate-600 ml-auto px-2 flex-1 whitespace-nowrap text-right">
        Sync pricing
      </div>
    </div>
  );
}

function SyncSubscriptionDialog({ onClose, product = {} }) {
  const queryClient = useQueryClient();
  const [syncSubscriptionState, setSyncSubscriptionState] = useState('initial'); // initial, syncing, synced, error
  const [createStripePriceError, setCreateStripePriceError] = useState('');
  const { token } = useAuth();

  const createStripePriceMutation = useMutation({
    mutationFn: async () => {
      setSyncSubscriptionState('syncing');

      return fetcher(
        `/sellers/${product.seller}/products/${product._id}/create-stripe-price`,
        token,
        {
          method: 'POST',
        },
      );
    },
    onSuccess: () => {
      setSyncSubscriptionState('synced');
      queryClient.invalidateQueries({ queryKey: pagesKey });
      window.setTimeout(() => {
        onClose();
        setSyncSubscriptionState('initial');
      }, 2000);
    },
    onError: () => {
      setCreateStripePriceError(
        'Failed to sync subscription plan. Please try again or contact support.',
      );
      setSyncSubscriptionState('error');
    },
  });

  return (
    <div className="text-center">
      <div className="flex justify-center w-full mt-6 mb-8">
        <div className="flex flex-row justify-between w-full relative gap-x-10">
          <div className="w-full flex items-center border border-gray-100 rounded p-4 bg-white z-10">
            <div className="h-auto mx-auto">
              <Logo size={80} />
            </div>
          </div>
          <div className="bg-gray-100 my-auto absolute top-0 bottom-0 w-full h-px" />
          <div className="w-full flex items-center border border-gray-100 rounded p-4 bg-white z-10">
            <img className="h-20 mx-auto" src={StripeLogo} />
          </div>
        </div>
      </div>

      <h2 className="text-3xl mb-2">Sync with Stripe</h2>

      <p className="text-gray-800 text-xl mb-8 max-w-lg mx-auto">
        This subscription plan does not yet exist in Stripe. Please sync it before sharing this
        checkout.
      </p>

      <div className="space-y-2 mb-4">
        <Button
          type="button"
          onClick={() => createStripePriceMutation.mutate()}
          appearance="primary"
          disabled={syncSubscriptionState === 'syncing' || syncSubscriptionState === 'synced'}
        >
          {(syncSubscriptionState === 'initial' || syncSubscriptionState === 'error') &&
            'Sync subscription plan'}

          {syncSubscriptionState === 'syncing' && (
            <>
              Syncing subscription plan <Spinner className="ml-2 h-5 w-5" appearance="light" />
            </>
          )}

          {syncSubscriptionState === 'synced' && (
            <>
              Subscription plan synced <CheckCircleIcon className="ml-2 h-5 w-5 text-white" />
            </>
          )}
        </Button>

        <FormErrorBase>{createStripePriceError}</FormErrorBase>
      </div>

      <button onClick={onClose}>
        <Link variant="secondary">Do this later</Link>
      </button>
    </div>
  );
}

export default CheckoutShareDialog;
